import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { FiTool, FiAlertTriangle, FiCheckCircle } from "react-icons/fi";
import { BsFileEarmarkArrowDown } from "react-icons/bs";
import Header from "../component/Header";
import { API_BASE_URL } from "../utils/apiConfig";

function MaintenancePage() {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [devices, setDevices] = useState([]);
    const [selectedDeviceId, setSelectedDeviceId] = useState("");

    const fetchLogs = useCallback(async (deviceId) => {
        setLoading(true);
        try {
            const token = localStorage.getItem("token");
            const config = { headers: { Authorization: `Bearer ${token}` } };
            const deviceParam = deviceId ? `?device_id=${deviceId}` : "";
            const response = await axios.get(`${API_BASE_URL}/api/maintenance${deviceParam}`, config);
            setLogs(response.data);
        } catch (err) {
            console.error("Failed to fetch maintenance logs", err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        const init = async () => {
            try {
                const token = localStorage.getItem("token");
                const config = { headers: { Authorization: `Bearer ${token}` } };
                const devicesRes = await axios.get(`${API_BASE_URL}/api/devices`, config);
                setDevices(devicesRes.data);
                
                const userRole = localStorage.getItem("role");
                let initialId = "";
                if (userRole !== "superadmin") {
                    initialId = devicesRes.data?.[0]?.device_id_PK || "";
                }
                
                setSelectedDeviceId(initialId);
                fetchLogs(initialId);
            } catch (err) {
                console.error("Failed to init maintenance", err);
                fetchLogs("");
            }
        };
        init();
    }, [fetchLogs]);

    const handleDeviceChange = (e) => {
        const id = e.target.value;
        setSelectedDeviceId(id);
        fetchLogs(id);
    };

    const overdueCount = logs.filter(l => l.status.toLowerCase() === 'overdue').length;
    const upToDateCount = logs.filter(l => l.status.toLowerCase() === 'completed').length;

    if (loading) return <div className="bg-black min-h-screen text-white flex items-center justify-center font-bold">Synchronizing Maintenance Logs...</div>;

    return (
        <div className="bg-black min-h-screen text-white p-6">
            <Header />

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
                <div className="bg-[#1b1d1a] rounded-2xl p-6 border border-gray-700 shadow-lg hover:shadow-xl transition">
                    <div className="flex items-start gap-3">
                        <FiTool className="text-green-400 text-3xl mt-3" />
                        <div>
                            <p className="text-gray-400 text-sm">Total Logs</p>
                            <h2 className="text-green-400 text-3xl font-bold mt-1">{logs.length}</h2>
                        </div>
                    </div>
                </div>
                <div className="bg-[#1b1d1a] rounded-2xl p-6 border border-gray-700 shadow-lg hover:shadow-xl transition">
                    <div className="flex items-start gap-3">
                        <FiCheckCircle className="text-green-400 text-3xl mt-3" />
                        <div>
                            <p className="text-gray-400 text-sm">Up to Date</p>
                            <h2 className="text-green-400 text-3xl font-bold mt-1">{upToDateCount}</h2>
                        </div>
                    </div>
                </div>
                <div className="bg-[#1b1d1a] rounded-2xl p-6 border border-gray-700 shadow-lg hover:shadow-xl transition">
                    <div className="flex items-start gap-3">
                        <FiAlertTriangle className={`text-3xl mt-3 ${overdueCount > 0 ? 'text-red-500' : 'text-gray-600'}`} />
                        <div>
                            <p className="text-gray-400 text-sm">Overdue Attention</p>
                            <h2 className={`${overdueCount > 0 ? 'text-red-500' : 'text-white'} text-3xl font-bold mt-1`}>{overdueCount}</h2>
                        </div>
                    </div>
                </div>
            </div>

            {/* Table Section */}
            <div className="grid grid-cols-1 gap-6 mt-8">
                <div className="bg-[#1b1d1a] rounded-2xl p-6 border border-gray-700 shadow-lg">
                    <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
                        <div>
                            <h2 className="text-xl font-bold text-green-400">Maintenance Schedule</h2>
                            <p className="text-gray-500 text-xs mt-1">Showing {logs.length} records for selected machine</p>
                        </div>

                        <div className="flex items-center gap-3 flex-wrap">
                            {/* ── Device Selector (same style as InventoryPage) ── */}
                            {devices.length > 0 && (
                                <select
                                    value={selectedDeviceId}
                                    onChange={handleDeviceChange}
                                    className="bg-[#1b1d1a] border border-gray-700 text-white rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 transition"
                                >
                                    <option value="">All Devices</option>
                                    {devices.map(device => (
                                        <option key={device.device_id_PK} value={device.device_id_PK}>
                                            {device.device_name}
                                        </option>
                                    ))}
                                </select>
                            )}

                            <button className="flex items-center gap-2 px-4 py-2 text-sm border border-green-400 rounded-lg text-green-400 hover:bg-green-400 hover:text-black transition">
                                <BsFileEarmarkArrowDown />
                                Generate Report
                            </button>
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm text-gray-300">
                            <thead>
                                <tr className="border-b border-gray-700 text-gray-400">
                                    <th className="px-4 py-3 text-left">Machine</th>
                                    <th className="px-4 py-3 text-left">Location</th>
                                    <th className="px-4 py-3 text-left">Last Service</th>
                                    <th className="px-4 py-3 text-left">Next Scheduled</th>
                                    <th className="px-4 py-3 text-left">Technician</th>
                                    <th className="px-4 py-3 text-left">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {logs.map((log) => (
                                    <tr key={log.maintenance_id_PK} className="border-b border-gray-800 hover:bg-[#1e1e1e] transition">
                                        <td className="px-4 py-3">
                                            <span className="px-3 py-1 text-xs font-semibold rounded-full border border-green-400 text-green-400">
                                                {log.device_name}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-gray-400">{log.location}</td>
                                        <td className="px-4 py-3 text-white">{new Date(log.last_cleaned).toLocaleDateString()}</td>
                                        <td className="px-4 py-3 text-white">{new Date(log.next_scheduled).toLocaleDateString()}</td>
                                        <td className="px-4 py-3 text-white">{log.technician_name || 'N/A'}</td>
                                        <td className="px-4 py-3">
                                            {log.status.toLowerCase() === "completed" ? (
                                                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold bg-green-400 text-black">
                                                    <FiCheckCircle /> Completed
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold bg-red-500 text-white">
                                                    <FiAlertTriangle /> Overdue
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                                {logs.length === 0 && (
                                    <tr>
                                        <td colSpan="6" className="px-4 py-8 text-center text-gray-500 italic">No maintenance logs found for this device</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default MaintenancePage;
